import React, { useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import SEO from '../components/SEO';
import { safeRedirectPath } from '../components/site/auth/authLinks';
import { redactUrl } from '../lib/urlPrivacy';
import { clearUrlSecret, readUrlSecret, stashUrlSecret } from '../lib/urlSecrets';

/**
 * Direct Signup Page
 * 
 * This page opens the signup modal automatically when visited.
 * Shareable link: https://tappd.app/signup
 * 
 * Also handles business invite signups:
 * - /signup?invite={token}&business={id}
 * - Stores invite token in sessionStorage for AuthContext to use
 * 
 * - If user is already logged in, redirects to deal wallet (or business dashboard if invited)
 * - If not logged in, opens the signup modal and shows home page
 */
const SignupPage = () => {
  const { isAuthenticated, loading, openAuthModal, setPostAuthRedirect } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Wait for the stored session check (GET /auth/me): deciding before it resolves sent a
    // signed-in visitor with an invite link to / with the sign-up dialog, not to the invite.
    if (loading) return;
    // Capture business invite token from URL params BEFORE any redirect. The index.html URL
    // secret guard normally took `?invite=` out of the address bar before any analytics tag
    // loaded and kept it for this path (lib/urlSecrets); a live `?invite=` (an older tab) is
    // kept the same way here, and never carried on into another URL.
    const inviteToken = readUrlSecret('invite', { location });
    const businessId = searchParams.get('business');
    const search = redactUrl(location.search, ['invite']) || '';
    if (inviteToken && searchParams.has('invite')) stashUrlSecret('invite', inviteToken, { pathname: location.pathname });
    
    if (inviteToken) {
      // Store invite token in sessionStorage for AuthContext.signup() to use
      sessionStorage.setItem('pending_business_invite_token', inviteToken);
      
      // Optionally store business ID for reference
      if (businessId) {
        sessionStorage.setItem('pending_business_invite_business_id', businessId);
      }
      
    }

    
    if (isAuthenticated) {
      // Already logged in - check if there's a pending invite to handle
      if (inviteToken) {
        // User is logged in but clicked an invite link - redirect to accept page, the token
        // kept for that page rather than put in its URL (in the URL only if storage fails)
        const kept = stashUrlSecret('token', inviteToken, { pathname: '/accept-business-invite' });
        clearUrlSecret('invite');
        navigate(kept ? '/accept-business-invite' : `/accept-business-invite?token=${encodeURIComponent(inviteToken)}`, { replace: true });
      } else {
        // Normal case - go to deal wallet
        navigate('/deal-wallet', { replace: true });
      }
    } else {
      // Keep whatever context the link carried (a plan intent, a campaign tag)
      // so signing up lands back here rather than on a bare home page. `?redirect=` is
      // followed only when it is a same-origin path (never another site).
      const redirect = safeRedirectPath(searchParams.get('redirect'));
      setPostAuthRedirect?.(redirect || `/signup${search}`);
      openAuthModal('signup');
      // Navigate to home page (modal will overlay it), query string intact (without `invite`).
      navigate({ pathname: '/', search }, { replace: true });
    }
  }, [loading, isAuthenticated, navigate, openAuthModal, searchParams, location.search, setPostAuthRedirect]);

  // Show nothing while redirecting; the head still gets this URL's own title, description
  // and canonical, marked noindex because the page redirects to / (not in the sitemap).
  return (
    <SEO
      title="Sign up"
      description="Create a free Tappd account. Find local deals from the QR coasters at bars and restaurants near you."
      url="/signup"
      noIndex
    />
  );
};

export default SignupPage;
